import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { getSession, logout } from '@/domains/auth/services/auth.service';
import {
  fetchClientOrders,
  fetchClientAddresses,
  fetchNotifications,
  fetchUserProfile
} from '@/domains/client/services/client.service';

import { getErrorMessage } from '@/utils/apiErrorHandler';
import { ErrorAlert } from '@/components/common/ErrorAlert';

import type { Order, Address, Notification, UserProfile } from '@/domains/client/services/client.service';

// Import des composants spécifiques existants
import OrderList from './components/OrderList';
import SavedAddresses from './components/SavedAddresses';
import NotificationList from './components/NotificationList';
import NewOrderWizard from './components/NewOrderWizard';
import RealTimeTracking from '@/components/features/RealTimeTracking';
import AccountSettings from './components/AccountSettings';
import ProfileSection from './components/ProfileSection';
import ComplaintForm from './components/ComplaintForm';
import OrderHistory from './components/OrderHistory';

// Type local pour les évaluations livreurs
interface DeliveryRating {
  id: string;
  orderId: string;
  orderCode: string;
  driverName: string;
  driverType: 'communautaire' | 'professionnel';
  rating: number;
  comment: string;
  date: string;
}

const TABS = [
  { id: 'commandes', label: 'En cours', icon: 'ri-truck-line' },
  { id: 'suivi', label: 'Suivi live', icon: 'ri-map-pin-line' },
  { id: 'marche', label: 'Marché Communautaire', icon: 'ri-store-2-line' },
  { id: 'portefeuille', label: 'Portefeuille', icon: 'ri-wallet-3-line' },
  { id: 'adresses', label: 'Adresses', icon: 'ri-map-pin-2-line' },
  { id: 'historique', label: 'Historique', icon: 'ri-history-line' },
  { id: 'evaluations', label: 'Évaluations', icon: 'ri-star-smile-line' },
  { id: 'litiges', label: 'Litiges & SAV', icon: 'ri-customer-service-2-line' },
  { id: 'notifications', label: 'Alertes', icon: 'ri-notification-line' },
  { id: 'profil', label: 'Profil', icon: 'ri-user-line' },
];

const PREDEFINED_OPTIONS = [
  { id: 'colis', label: 'Colis Personnel', icon: 'ri-box-3-line', color: 'bg-teal-50 text-teal-700' },
  { id: 'documents', label: 'Documents / Papiers', icon: 'ri-file-text-line', color: 'bg-blue-50 text-blue-700' },
  { id: 'alimentaire', label: 'Nourriture / Sacs', icon: 'ri-restaurant-line', color: 'bg-amber-50 text-amber-700' },
  { id: 'medicaments', label: 'Médicaments / Santé', icon: 'ri-capsule-line', color: 'bg-emerald-50 text-emerald-700' },
  { id: 'vetements', label: 'Habits / Pagnes', icon: 'ri-shirt-line', color: 'bg-purple-50 text-purple-700' },
  { id: 'electronique', label: 'Téléphone / Matériel', icon: 'ri-smartphone-line', color: 'bg-rose-50 text-rose-700' }
];

export default function ClientDashboard() {
  const navigate = useNavigate();

  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [activeTab, setActiveTab] = useState('commandes');
  const [error, setError] = useState('');

  // Données API
  const [orders, setOrders] = useState<Order[]>([]);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const [ordersLoading, setOrdersLoading] = useState(false);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  // Évaluations livreurs (mock)
  const [ratings, setRatings] = useState<DeliveryRating[]>([]);
  const [ratingsLoading, setRatingsLoading] = useState(false);

  // Modals
  const [showNewOrderWizard, setShowNewOrderWizard] = useState(false);
  const [showComplaintForm, setShowComplaintForm] = useState(false);
  const [selectedComplaintOrderId, setSelectedComplaintOrderId] = useState<string>('');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false); // ÉTAT CONFIRMATION DECONNEXION

  // Cycle de chargement initial sécurisé
  useEffect(() => {
    const session = getSession();
    if (!session) {
      navigate('/login');
      return;
    }

    const loadProfile = async () => {
      try {
        const profile = await fetchUserProfile();
        setUserProfile(profile);
      } catch (err) {
        setError(getErrorMessage(err));
      }
    };
    loadProfile();
  }, [navigate]);

  // Chargement contextuel strict selon l'onglet requis
  useEffect(() => {
    if (['commandes', 'historique', 'suivi', 'litiges'].includes(activeTab) && orders.length === 0) {
      loadOrders();
    }
    if (activeTab === 'adresses' && addresses.length === 0) loadAddresses();
    if (activeTab === 'notifications' && notifications.length === 0) loadNotifications();
    if (activeTab === 'evaluations' && ratings.length === 0) loadRatings();
  }, [activeTab]);

  const loadOrders = async () => {
    setOrdersLoading(true);
    try {
      const data = await fetchClientOrders();
      setOrders(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setOrdersLoading(false);
    }
  };

  const loadAddresses = async () => {
    setAddressesLoading(true);
    try {
      const data = await fetchClientAddresses();
      setAddresses(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setAddressesLoading(false);
    }
  };

  const loadNotifications = async () => {
    setNotificationsLoading(true);
    try {
      const data = await fetchNotifications();
      setNotifications(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setNotificationsLoading(false);
    }
  };

  const loadRatings = () => {
    setRatingsLoading(true);
    setTimeout(() => {
      setRatings([
        { id: 'R-001', orderId: '101', orderCode: 'ONA-2026-0041', driverName: 'Mamadou Camara', driverType: 'communautaire', rating: 5, comment: 'Très rapide et soigneux, je recommande !', date: '04/06/2026' },
        { id: 'R-002', orderId: '102', orderCode: 'ONA-2026-0089', driverName: 'Ibrahima Sow', driverType: 'professionnel', rating: 4, comment: 'Bon service, léger retard.', date: '02/06/2026' },
        { id: 'R-003', orderId: '103', orderCode: 'ONA-2026-0012', driverName: 'Aissatou Barry', driverType: 'communautaire', rating: 5, comment: 'Excellente livraison, colis intact.', date: '28/05/2026' },
      ]);
      setRatingsLoading(false);
    }, 300);
  };

  const handleFileComplaint = (orderId: string) => {
    setSelectedComplaintOrderId(orderId);
    setShowComplaintForm(true);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error("Erreur lors de la déconnexion backend:", err);
    } finally {
      localStorage.removeItem('token');
      sessionStorage.clear();
      setShowLogoutConfirm(false);
      navigate('/login');
    }
  };

  // Calculs logistiques globaux
  const unreadCount = notifications.filter(n => !n.est_lu).length;
  const activeOrders = orders.filter(o => ['en_cours', 'en_transit', 'en_attente', 'pending'].includes(o.statut));
  const completedOrders = orders.filter(o => o.statut === 'livree').length;
  const avgRating = ratings.length > 0 ? (ratings.reduce((acc, r) => acc + r.rating, 0) / ratings.length).toFixed(1) : '—';

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans pb-32 md:pb-8 md:pl-64 flex flex-col">

      {/* ── INTERFACE SIDEBAR : DESKTOP ───────────────────────────────── */}
      <aside className="hidden md:flex flex-col fixed inset-y-0 left-0 w-64 bg-slate-950 text-slate-200 z-30 shadow-xl">
        <div className="p-6 border-b border-slate-900 flex items-center space-x-3">
          <div className="w-10 h-10 bg-teal-500 rounded-xl flex items-center justify-center text-slate-950 font-black text-xl shadow-lg shadow-teal-500/20">
            O
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-lg tracking-tight text-white leading-none">ONAPILA</span>
            <span className="text-[10px] text-teal-400 font-medium tracking-widest uppercase mt-1">Livrer easy</span>
          </div>
        </div>

        {/* Liens de Navigation */}
        <div className="p-4 flex-1 space-y-1 overflow-y-auto custom-scrollbar">
          {TABS.map((tab) => {
            const isSelected = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-xs font-semibold transition-all group ${isSelected
                  ? 'bg-teal-500 text-slate-950 shadow-md shadow-teal-500/10'
                  : 'text-slate-400 hover:bg-slate-900 hover:text-slate-100'
                  }`}
              >
                <div className="flex items-center space-x-3">
                  <i className={`${tab.icon} text-base ${isSelected ? 'text-slate-950' : 'text-slate-400 group-hover:text-slate-200'}`}></i>
                  <span>{tab.label}</span>
                </div>
                {tab.id === 'notifications' && unreadCount > 0 && (
                  <span className={`px-2 py-0.5 text-[10px] rounded-full ${isSelected ? 'bg-slate-950 text-teal-400' : 'bg-teal-500/20 text-teal-400'}`}>
                    {unreadCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Profil Section Bas Sidebar */}
        {userProfile && (
          <div className="p-4 border-t border-slate-900 bg-slate-900/40 flex items-center space-x-3">
            <div className="w-9 h-9 rounded-xl bg-teal-500 text-slate-950 flex items-center justify-center font-bold text-xs">
              {userProfile.firstnames[0]}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-white truncate">{userProfile.firstnames} {userProfile.lastname}</p>
              <p className="text-[10px] text-slate-500 truncate">{userProfile.zone?.name || 'Axe national'}</p>
            </div>
            <button
              onClick={() => setActiveTab('parametres')}
              className="p-1 text-slate-400 hover:text-white rounded-lg transition-colors"
            >
              <i className="ri-settings-4-line text-base"></i>
            </button>
            {/* BOUTON DE DECONNEXION ASSIGNÉ */}
            <button
              onClick={() => setShowLogoutConfirm(true)}
              className="p-1.5 text-rose-400 hover:text-rose-300 hover:bg-rose-950/30 rounded-lg transition-colors"
              title="Déconnexion"
            >
              <i className="ri-logout-box-r-line text-xs"></i>
            </button>
          </div>
        )}
      </aside>

      {/* ── INTERFACE TOPBAR ─────────────────────────── */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-20 shadow-sm px-4 py-4 md:px-8">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3 md:space-x-0">
            <div className="md:hidden w-8 h-8 bg-slate-900 text-teal-400 rounded-lg flex items-center justify-center font-bold text-sm">O</div>
            <div>
              <h1 className="text-sm font-bold text-slate-800 md:text-xl md:font-extrabold md:tracking-tight">
                {activeTab === 'commandes' && 'Suivi de vos expéditions'}
                {activeTab === 'suivi' && 'Positionnement GPS en Direct'}
                {activeTab === 'adresses' && 'Carnet d\'adresses logistiques'}
                {activeTab === 'historique' && 'Archives de livraisons'}
                {activeTab === 'evaluations' && 'Évaluations et Avis de nos Livreurs'}
                {activeTab === 'litiges' && 'Service Après-Vente & Réclamations'}
                {activeTab === 'notifications' && 'Centre d\'alertes'}
                {activeTab === 'profil' && 'Mon Profil Utilisateur'}
                {activeTab === 'parametres' && 'Configurations'}
              </h1>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <div className="hidden sm:flex items-center bg-slate-100 px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600">
              <i className="ri-global-line text-teal-600 mr-1.5 text-sm"></i>
              Zone : {userProfile?.zone?.name || 'Guinée (Conakry)'}
            </div>
          </div>
        </div>
      </header>

      {/* ── ZONE DE CONTENU CENTRAL SWITCHER MÉTIEZ ────────────────── */}
      <main className="flex-1 w-full px-4 md:px-4 py-4 md:py-6">
        {error && <ErrorAlert error={error} onClose={() => setError('')} />}

        <div className="min-h-[500px]">

          {/* 1. ONGLET: COMMANDES COURANTES (URBAIN) */}
          {activeTab === 'commandes' && (
            <div className="space-y-6 animate-fadeIn">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex items-center space-x-3.5">
                  <div className="w-10 h-10 bg-teal-50 text-teal-600 rounded-xl flex items-center justify-center text-xl"><i className="ri-box-3-line"></i></div>
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">En cours</p>
                    <p className="text-xl font-bold text-slate-800 mt-0.5">{activeOrders.length}</p>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex items-center space-x-3.5">
                  <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center text-xl"><i className="ri-check-double-line"></i></div>
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Livrées</p>
                    <p className="text-xl font-bold text-slate-800 mt-0.5">{completedOrders}</p>
                  </div>
                </div>
                <div className="col-span-2 md:col-span-1 bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex items-center space-x-3.5">
                  <div className="w-10 h-10 bg-amber-50 text-amber-600 rounded-xl flex items-center justify-center text-xl"><i className="ri-star-line"></i></div>
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Note moyenne</p>
                    <p className="text-sm font-bold text-slate-800 mt-0.5">{avgRating} / 5</p>
                  </div>
                </div>
              </div>

              {/* Action Principal */}
              <div className="bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 rounded-2xl p-6 text-white border border-slate-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <span className="bg-teal-500/10 text-teal-400 border border-teal-500/20 px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider">ONAPILA Delivery Engine</span>
                  <h2 className="text-base font-bold tracking-tight mt-2">Nouvel Envoi Immédiat</h2>
                  <p className="text-xs text-slate-400 mt-1">Expédition instantanée par coursier (Urbaine ou Interurbaine).</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setShowNewOrderWizard(true)} className="px-4 py-2.5 bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold rounded-xl text-xs transition-all shadow-md shadow-teal-500/10">Demande de courses</button>
                </div>
              </div>

              {/* Raccourcis Catégories */}
              <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">Raccourcis types de marchandises</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
                  {PREDEFINED_OPTIONS.map((option) => (
                    <button key={option.id} onClick={() => setShowNewOrderWizard(true)} className="flex flex-col items-center justify-center p-3 rounded-xl border border-slate-100 hover:border-slate-300 bg-slate-50/50 hover:bg-white transition-all text-center group">
                      <div className={`w-10 h-10 ${option.color} rounded-lg flex items-center justify-center text-lg transition-transform group-hover:scale-105`}><i className={option.icon}></i></div>
                      <span className="text-xs font-semibold text-slate-700 mt-2 leading-tight">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <OrderList orders={orders} loading={ordersLoading} onNewOrder={() => setShowNewOrderWizard(true)} onTrackOrder={() => setActiveTab('suivi')} />
            </div>
          )}

          {/* 2. ONGLET: SUIVI LIVE GPS */}
          {activeTab === 'suivi' && (
            <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm animate-fadeIn">
              <div className="flex items-center space-x-2 mb-4">
                <span className="flex h-2 w-2 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500"></span>
                </span>
                <h2 className="text-sm font-bold text-slate-800">Géolocalisation en temps réel</h2>
              </div>
              {orders.length > 0 ? (
                <RealTimeTracking orderId={orders.find(o => o.statut !== 'livree')?.id || orders[0].id} />
              ) : (
                <div className="text-center py-12 text-slate-400">
                  <i className="ri-map-2-line text-3xl block mb-2"></i>
                  <p className="text-xs">Aucune expédition active n'est traçable par satellite actuellement.</p>
                </div>
              )}
            </div>
          )}

          {/* 3. ONGLET: ÉVALUATIONS & AVIS */}
          {activeTab === 'evaluations' && (
            <div className="space-y-6 animate-fadeIn">
              <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                  <div>
                    <h2 className="text-base font-bold text-slate-800">Évaluations et Avis</h2>
                    <p className="text-xs text-slate-400 mt-0.5">Notez les livreurs pour garantir la qualité du service ONAPILA.</p>
                  </div>
                  <div className="flex items-center space-x-2 text-xs font-semibold text-amber-600 bg-amber-50 px-3 py-1.5 rounded-xl border border-amber-100">
                    <i className="ri-star-fill"></i>
                    <span>Moyenne générale: {avgRating} / 5</span>
                  </div>
                </div>

                {ratingsLoading ? (
                  <div className="py-12 text-center text-xs text-slate-400 animate-pulse">Chargement des avis...</div>
                ) : ratings.length === 0 ? (
                  <div className="py-12 text-center text-xs text-slate-400">Aucune évaluation enregistrée.</div>
                ) : (
                  <div className="space-y-4">
                    {ratings.map((r) => (
                      <div key={r.id} className="p-4 border border-slate-100 rounded-2xl hover:bg-slate-50/50 transition-colors flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="space-y-1">
                          <div className="flex items-center space-x-2">
                            <span className="font-bold text-slate-800 text-sm">{r.driverName}</span>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${r.driverType === 'professionnel' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                              Livreur {r.driverType}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 italic">"{r.comment}"</p>
                          <p className="text-[10px] text-slate-400">Commande {r.orderCode} &bull; le {r.date}</p>
                        </div>
                        <div className="flex items-center space-x-1">
                          {[...Array(5)].map((_, i) => (
                            <i key={i} className={`ri-star-${i < r.rating ? 'fill' : 'line'} text-base ${i < r.rating ? 'text-amber-400' : 'text-slate-300'}`}></i>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 6. ONGLET: MARCHÉ COMMUNAUTAIRE */}
          {activeTab === 'marche' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 rounded-2xl p-6 text-white">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <span className="bg-white/20 text-white border border-white/30 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider">
                      Voisinage
                    </span>
                    <h2 className="text-base font-bold tracking-tight mt-2">Marché Communautaire</h2>
                    <p className="text-xs text-teal-100 mt-1">
                      Réservez des livraisons sur les trajets de vos voisins
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
                <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center space-x-2">
                  <i className="ri-road-map-line text-teal-500"></i>
                  <span>Trajets disponibles</span>
                </h3>
                <div className="space-y-3">
                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center text-teal-600">
                          <i className="ri-truck-line"></i>
                        </div>
                        <div>
                          <p className="text-xs font-bold text-slate-800">Conakry → Labé</p>
                          <p className="text-[10px] text-slate-500">Ce vendredi, 8h00 • 5kg disponible</p>
                        </div>
                      </div>
                      <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-[10px] font-bold">15 000 GNF</span>
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200">
                      <div className="flex items-center space-x-2 text-[10px] text-slate-500">
                        <span><i className="ri-user-line mr-1"></i>Mamadou D.</span>
                        <span><i className="ri-star-fill text-amber-400 mr-1"></i>4.8</span>
                      </div>
                      <button className="px-3 py-1.5 bg-teal-600 text-white text-[10px] font-bold rounded-lg hover:bg-teal-700">Réserver</button>
                    </div>
                  </div>

                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center text-teal-600">
                          <i className="ri-truck-line"></i>
                        </div>
                        <div>
                          <p className="text-xs font-bold text-slate-800">Conakry → Kankan</p>
                          <p className="text-[10px] text-slate-500">Samedi prochain, 6h00 • 10kg disponible</p>
                        </div>
                      </div>
                      <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-[10px] font-bold">25 000 GNF</span>
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200">
                      <div className="flex items-center space-x-2 text-[10px] text-slate-500">
                        <span><i className="ri-user-line mr-1"></i>Fatou B.</span>
                        <span><i className="ri-star-fill text-amber-400 mr-1"></i>4.9</span>
                      </div>
                      <button className="px-3 py-1.5 bg-teal-600 text-white text-[10px] font-bold rounded-lg hover:bg-teal-700">Réserver</button>
                    </div>
                  </div>

                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 opacity-60">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-slate-200 rounded-xl flex items-center justify-center text-slate-500">
                          <i className="ri-truck-line"></i>
                        </div>
                        <div>
                          <p className="text-xs font-bold text-slate-800">Conakry → Mamou</p>
                          <p className="text-[10px] text-slate-500">Dimanche, 7h00 • Plein</p>
                        </div>
                      </div>
                      <span className="px-2 py-1 bg-slate-200 text-slate-600 rounded-full text-[10px] font-bold">Complet</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
                <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center space-x-2">
                  <i className="ri-information-line text-blue-500"></i>
                  <span>Comment ça marche ?</span>
                </h3>
                <div className="space-y-2 text-xs text-slate-600">
                  <div className="flex items-start space-x-2">
                    <span className="w-5 h-5 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 text-[10px] font-bold flex-shrink-0">1</span>
                    <p>Consultez les trajets planifiés par vos voisins</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="w-5 h-5 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 text-[10px] font-bold flex-shrink-0">2</span>
                    <p>Réservez un emplacement pour votre colis</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="w-5 h-5 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 text-[10px] font-bold flex-shrink-0">3</span>
                    <p>Déposez votre colis au point de rencontre</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="w-5 h-5 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 text-[10px] font-bold flex-shrink-0">4</span>
                    <p>Suivez la livraison en temps réel</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 7. ONGLET: PORTEFEUILLE & PAIEMENTS */}
          {activeTab === 'portefeuille' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-indigo-600 via-indigo-700 to-indigo-800 rounded-2xl p-6 text-white">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <span className="bg-white/20 text-white border border-white/30 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider">
                      Paiements
                    </span>
                    <h2 className="text-base font-bold tracking-tight mt-2">Portefeuille & Suivi</h2>
                    <p className="text-xs text-indigo-100 mt-1">
                      Suivez vos paiements COD et reversals Mobile Money
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-600">
                      <i className="ri-money-dollar-circle-line"></i>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-slate-800">125 000 GNF</p>
                      <p className="text-[10px] text-slate-500">Solde disponible</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center text-orange-600">
                      <i className="ri-orange-money-line"></i>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-slate-800">45 000 GNF</p>
                      <p className="text-[10px] text-slate-500">Orange Money</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center text-yellow-600">
                      <i className="ri-money-dollar-circle-line"></i>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-slate-800">30 000 GNF</p>
                      <p className="text-[10px] text-slate-500">MTN MoMo</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
                <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center space-x-2">
                  <i className="ri-history-line text-indigo-500"></i>
                  <span>Transactions récentes</span>
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600">
                        <i className="ri-arrow-down-line"></i>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-800">Reçu COD - CMD-2024-001234</p>
                        <p className="text-[10px] text-slate-500">Aujourd'hui, 14:30</p>
                      </div>
                    </div>
                    <span className="text-xs font-bold text-emerald-600">+15 000 GNF</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center text-orange-600">
                        <i className="ri-orange-money-line"></i>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-800">Reversal Orange Money</p>
                        <p className="text-[10px] text-slate-500">Hier, 09:15</p>
                      </div>
                    </div>
                    <span className="text-xs font-bold text-emerald-600">+25 000 GNF</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-rose-100 rounded-lg flex items-center justify-center text-rose-600">
                        <i className="ri-arrow-up-line"></i>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-800">Paiement commande - CMD-2024-001231</p>
                        <p className="text-[10px] text-slate-500">Hier, 08:45</p>
                      </div>
                    </div>
                    <span className="text-xs font-bold text-rose-600">-12 500 GNF</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
                <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center space-x-2">
                  <i className="ri-information-line text-blue-500"></i>
                  <span>Informations de paiement</span>
                </h3>
                <div className="space-y-2 text-xs text-slate-600">
                  <div className="flex items-start space-x-2">
                    <span className="w-5 h-5 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 text-[10px] font-bold flex-shrink-0">1</span>
                    <p>Les paiements COD sont collectés par le livreur et crédités sur votre portefeuille</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="w-5 h-5 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 text-[10px] font-bold flex-shrink-0">2</span>
                    <p>Les reversals Mobile Money sont traités sous 24-48h ouvrées</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="w-5 h-5 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 text-[10px] font-bold flex-shrink-0">3</span>
                    <p>Vous pouvez demander un retrait à tout moment via le support client</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 8. ONGLET: ADRESSES ENREGISTRÉES */}
          {activeTab === 'adresses' && (
            <SavedAddresses
              addresses={addresses.map(addr => ({
                ...addr,
                icon: 'ri-map-pin-line',
                point_repere: addr.point_repere ?? null,
                quartier: addr.quartier ?? null,
                ville: addr.ville ?? null,
                pays: addr.pays ?? null,
                code_postal: addr.code_postal ?? null,
              }))}
              loading={addressesLoading}
              error={error}
              onAddAddress={loadAddresses}
              onEditAddress={loadAddresses}
              onDeleteAddress={loadAddresses}
              onSelectAddress={() => { }}
            />
          )}

          {/* 7. ONGLET: HISTORIQUE TOTAL */}
          {activeTab === 'historique' && (
            <OrderHistory orders={orders} onFileComplaint={handleFileComplaint} />
          )}

          {/* 8. NOUVEAU ONGLET: LITIGES & RECLAMATIONS (SAV INTÉGRÉ) */}
          {activeTab === 'litiges' && (
            <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-6 animate-fadeIn">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h2 className="text-base font-bold text-slate-800">Support Client & Gestion des Litiges</h2>
                  <p className="text-xs text-slate-400 mt-0.5">Déclarez une anomalie sur un trajet, un problème d'encaissement de fonds ou un colis abîmé.</p>
                </div>
                <button onClick={() => { setSelectedComplaintOrderId(''); setShowComplaintForm(true); }} className="px-3 py-2 bg-rose-50 text-rose-700 border border-rose-100 font-bold rounded-xl text-xs">
                  Ouvrir un ticket de réclamation
                </button>
              </div>

              {orders.length > 0 ? (
                <div className="space-y-3">
                  <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Sélectionnez une commande récente à signaler :</p>
                  {orders.slice(0, 3).map((o) => (
                    <div key={o.id} className="flex justify-between items-center p-3 border border-slate-100 rounded-xl hover:bg-slate-50 text-xs">
                      <div>
                        <p className="font-bold text-slate-700">Commande N° {o.id}</p>
                        <p className="text-[10px] text-slate-400">Statut actuel : {o.statut} &bull; Prix : {o.prix_estime || 0} GNF</p>
                      </div>
                      <button onClick={() => handleFileComplaint(o.id as any)} className="px-2.5 py-1.5 bg-slate-900 text-white rounded-lg font-medium text-[11px]">Signaler un problème</button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-6 text-center text-xs text-slate-400">Aucune commande disponible pour réclamation.</div>
              )}
            </div>
          )}

          {/* 9. ONGLET: ALERTES ET NOTIFICATIONS */}
          {activeTab === 'notifications' && (
            <NotificationList notifications={notifications} loading={notificationsLoading} />
          )}

          {/* 10. ONGLET: PROFIL DE COMPTE */}
          {activeTab === 'profil' && userProfile && (
            <ProfileSection user={{
              id: userProfile.id,
              firstnames: userProfile.firstnames,
              lastname: userProfile.lastname,
              email: userProfile.email || '',
              phone: userProfile.phone,
              status: userProfile.status,
              zone: userProfile.zone
            }} />
          )}

          {/* 11. ONGLET: PARAMÈTRES TECHNIQUES */}
          {activeTab === 'parametres' && (
            <AccountSettings onSave={() => { }} />
          )}
        </div>
      </main>

      {/* ── NAV BAR DES SMARTPHONES ─── */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-slate-100 py-3 px-2 flex justify-around items-center z-30 shadow-[0_-8px_24px_rgba(0,0,0,0.06)]">
        <button onClick={() => setActiveTab('commandes')} className={`flex flex-col items-center space-y-0.5 ${activeTab === 'commandes' ? 'text-teal-600 font-bold' : 'text-slate-400'}`}>
          <i className="ri-truck-line text-lg"></i>
          <span className="text-[10px] tracking-tight font-medium">En cours</span>
        </button>
        <button onClick={() => setActiveTab('suivi')} className={`flex flex-col items-center space-y-0.5 ${activeTab === 'suivi' ? 'text-teal-600 font-bold' : 'text-slate-400'}`}>
          <i className="ri-map-pin-line text-lg"></i>
          <span className="text-[10px] tracking-tight font-medium">Suivi</span>
        </button>

        <button onClick={() => setShowNewOrderWizard(true)} className="w-12 h-12 bg-slate-900 text-teal-400 rounded-full flex items-center justify-center shadow-lg -translate-y-4 border-4 border-white transition-transform active:scale-95">
          <i className="ri-add-line text-xl font-bold"></i>
        </button>

        <button onClick={() => setActiveTab('evaluations')} className={`flex flex-col items-center space-y-0.5 ${activeTab === 'evaluations' ? 'text-teal-600 font-bold' : 'text-slate-400'}`}>
          <i className="ri-star-smile-line text-lg"></i>
          <span className="text-[10px] tracking-tight font-medium">Avis</span>
        </button>
        <button onClick={() => setActiveTab('litiges')} className={`flex flex-col items-center space-y-0.5 ${activeTab === 'litiges' ? 'text-teal-600 font-bold' : 'text-slate-400'}`}>
          <i className="ri-customer-service-2-line text-lg"></i>
          <span className="text-[10px] tracking-tight font-medium">SAV</span>
        </button>
        {/* Bouton déconnexion mobile visible dans la topbar */}
        <button
          onClick={() => setShowLogoutConfirm(true)}
          className="md:hidden p-2 text-slate-400 hover:text-rose-600 transition-colors"
        >
          <i className="ri-logout-box-r-line text-base"></i>
        </button>
      </nav>

      {/* Modals Transverses Complétés */}
      <NewOrderWizard isOpen={showNewOrderWizard} onClose={() => setShowNewOrderWizard(false)} savedAddresses={addresses} onOrderCreated={loadOrders} />
      <ComplaintForm orderId={selectedComplaintOrderId} isOpen={showComplaintForm} onClose={() => setShowComplaintForm(false)} onSubmit={() => { setShowComplaintForm(false); setActiveTab('litiges'); }} />

      {/* ── POPUP COMPOSANT DE CONFIRMATION DE DÉCONNEXION ────────────────── */}
      {
        showLogoutConfirm && (
          <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
            <div className="bg-white rounded-xl border border-slate-100 max-w-sm w-full p-5 shadow-2xl space-y-4">
              <div className="flex items-center space-x-3 text-rose-600">
                <div className="w-10 h-10 bg-rose-50 rounded-xl flex items-center justify-center text-lg">
                  <i className="ri-error-warning-line"></i>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900">Fermer la session ?</h3>
                  <p className="text-xs text-slate-400 mt-0.5">Vous devrez ressaisir vos identifiants ONAPILA.</p>
                </div>
              </div>

              <p className="text-xs text-slate-600 leading-relaxed">
                Êtes-vous sûr de vouloir vous déconnecter ?
              </p>

              <div className="flex space-x-2 pt-2">
                <button
                  onClick={() => setShowLogoutConfirm(false)}
                  className="flex-1 py-2 border border-slate-200 text-slate-700 hover:bg-slate-50 text-xs font-semibold rounded-lg transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleLogout}
                  className="flex-1 py-2 bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold rounded-lg shadow-md shadow-rose-600/10 transition-colors"
                >
                  Confirmer
                </button>
              </div>
            </div>
          </div>
        )
      }
    </div>
  );
}